/* eslint-disable @typescript-eslint/no-explicit-any */
// Note: We use 'any' types in this file to ensure compatibility across different Sentry SDK versions
// and to prevent logging errors from affecting the application

import * as Sentry from "@sentry/react";
import type { Span, SeverityLevel, Breadcrumb } from "@sentry/react";

export type LogLevel =
  | "trace"
  | "debug"
  | "info"
  | "warning"
  | "error"
  | "fatal";

export interface LogContext {
  tags?: Record<string, string | number | boolean | null | undefined>;
  extras?: Record<string, unknown>;
  fingerprint?: string[];
}

export interface LoggerSpan {
  setAttribute(key: string, value: string | number | boolean): void;
  addBreadcrumb(message: string, data?: Record<string, unknown>): void;
  end(status?: "ok" | "error", data?: Record<string, unknown>): void;
}

export interface LoggerTransaction extends LoggerSpan {
  startChildSpan(
    name: string,
    attrs?: Record<string, string | number | boolean>
  ): LoggerSpan;
}

export interface Logger {
  init(): void;
  setUser(
    user: { id?: string; email?: string; username?: string } | null
  ): void;
  setTags(
    tags: Record<string, string | number | boolean | null | undefined>
  ): void;
  setExtras(extras: Record<string, unknown>): void;
  clearScope(): void;

  addBreadcrumb: (
    message: string,
    data?: Record<string, unknown>,
    level?: LogLevel,
    category?: string
  ) => void;

  trace: (message: string, ctx?: LogContext) => void;
  debug: (message: string, ctx?: LogContext) => void;
  info: (message: string, ctx?: LogContext) => void;
  warn: (message: string, ctx?: LogContext) => void;
  error: (message: string, ctx?: LogContext) => void;
  fatal: (message: string, ctx?: LogContext) => void;

  captureException: (error: unknown, ctx?: LogContext) => void;
  captureMessage: (message: string, level?: LogLevel, ctx?: LogContext) => void;

  startTransaction: (
    name: string,
    attrs?: Record<string, string | number | boolean>
  ) => LoggerTransaction;

  increment: (
    metric: string,
    value?: number,
    tags?: Record<string, string>
  ) => void;
  distribution: (
    metric: string,
    value: number,
    tags?: Record<string, string>
  ) => void;
  gauge(metric: string, value: number, tags?: Record<string, string>): void;

  flush(timeout_ms?: number): Promise<boolean>;
}

class ConsoleSpan implements LoggerSpan {
  private name: string;
  private start_ms: number;

  constructor(name: string) {
    this.name = name;
    this.start_ms = performance.now();
  }

  setAttribute(key: string, value: string | number | boolean): void {
    try {
      console.debug(`[span] setAttribute`, { name: this.name, key, value });
    } catch {
      // Silently ignore console errors to prevent logging from affecting app
    }
  }

  addBreadcrumb(message: string, data?: Record<string, unknown>): void {
    try {
      console.debug(`[span] breadcrumb`, { name: this.name, message, data });
    } catch {
      // Silently ignore console errors to prevent logging from affecting app
    }
  }

  end(status: "ok" | "error" = "ok", data?: Record<string, unknown>): void {
    try {
      const duration_ms = performance.now() - this.start_ms;
      const payload = {
        name: this.name,
        status,
        duration_ms,
        ...data,
      };
      if (status === "error") {
        console.warn(`[span] end`, payload);
      } else {
        console.debug(`[span] end`, payload);
      }
    } catch {
      // Silently ignore console errors to prevent logging from affecting app
    }
  }
}

class ConsoleTransaction extends ConsoleSpan implements LoggerTransaction {
  startChildSpan(
    name: string,
    _attrs?: Record<string, string | number | boolean>
  ): LoggerSpan {
    try {
      return new ConsoleSpan(`${name}`);
    } catch {
      // Return a no-op span if creation fails
      return {
        setAttribute: () => {},
        addBreadcrumb: () => {},
        end: () => {},
      };
    }
  }
}

class ConsoleLogger implements Logger {
  init(): void {}
  setUser(
    _user: { id?: string; email?: string; username?: string } | null
  ): void {}
  setTags(
    _tags: Record<string, string | number | boolean | null | undefined>
  ): void {}
  setExtras(_extras: Record<string, unknown>): void {}
  clearScope(): void {}

  addBreadcrumb(
    message: string,
    data?: Record<string, unknown>,
    level: LogLevel = "info",
    category?: string
  ): void {
    try {
      const prefix = category ? `[${category}]` : "[breadcrumb]";
      if (level === "error") console.error(prefix, message, data);
      else if (level === "warning") console.warn(prefix, message, data);
      else console.debug(prefix, message, data);
    } catch {
      // Silently ignore console errors to prevent logging from affecting app
    }
  }

  trace(message: string, ctx?: LogContext): void {
    try {
      console.debug(`[trace] ${message}`, ctx);
    } catch {}
  }
  debug(message: string, ctx?: LogContext): void {
    try {
      console.debug(message, ctx);
    } catch {}
  }
  info(message: string, ctx?: LogContext): void {
    try {
      console.info(message, ctx);
    } catch {}
  }
  warn(message: string, ctx?: LogContext): void {
    try {
      console.warn(message, ctx);
    } catch {}
  }
  error(message: string, ctx?: LogContext): void {
    try {
      console.error(message, ctx);
    } catch {}
  }
  fatal(message: string, ctx?: LogContext): void {
    try {
      console.error(`[fatal] ${message}`, ctx);
    } catch {}
  }

  captureException(error: unknown, ctx?: LogContext): void {
    try {
      console.error(`[exception]`, error, ctx);
    } catch {
      // Silently ignore console errors to prevent logging from affecting app
    }
  }

  captureMessage(
    message: string,
    level: LogLevel = "info",
    ctx?: LogContext
  ): void {
    try {
      if (level === "error") console.error(`[message]`, message, ctx);
      else if (level === "warning") console.warn(`[message]`, message, ctx);
      else console.log(`[message]`, message, ctx);
    } catch {
      // Silently ignore console errors to prevent logging from affecting app
    }
  }

  startTransaction(
    name: string,
    _attrs?: Record<string, string | number | boolean>
  ): LoggerTransaction {
    try {
      return new ConsoleTransaction(name);
    } catch {
      // Return a no-op transaction if creation fails
      return {
        setAttribute: () => {},
        addBreadcrumb: () => {},
        end: () => {},
        startChildSpan: () => ({
          setAttribute: () => {},
          addBreadcrumb: () => {},
          end: () => {},
        }),
      };
    }
  }

  increment(
    _metric: string,
    _value: number = 1,
    _tags?: Record<string, string>
  ): void {}
  distribution(
    _metric: string,
    _value: number,
    _tags?: Record<string, string>
  ): void {}
  gauge(
    _metric: string,
    _value: number,
    _tags?: Record<string, string>
  ): void {}

  flush(_timeout_ms: number = 1500): Promise<boolean> {
    return Promise.resolve(true);
  }
}

class SentrySpan implements LoggerSpan {
  protected span: Span | undefined;
  protected name: string;

  constructor(span: Span | undefined, name: string) {
    this.span = span;
    this.name = name;
  }

  setAttribute(key: string, value: string | number | boolean): void {
    try {
      this.span?.setAttribute?.(key, value);
    } catch {}
  }

  addBreadcrumb(message: string, data?: Record<string, unknown>): void {
    try {
      const breadcrumb: Partial<Breadcrumb> = {
        message,
        data,
        category: this.name,
        level: "info" as SeverityLevel,
      };
      Sentry.addBreadcrumb(breadcrumb);
    } catch {}
  }

  end(status: "ok" | "error" = "ok", data?: Record<string, unknown>): void {
    try {
      if (data && this.span) {
        Object.entries(data).forEach(([k, v]) => {
          try {
            if (
              typeof v === "string" ||
              typeof v === "number" ||
              typeof v === "boolean"
            ) {
              this.span?.setAttribute?.(k, v);
            }
          } catch {}
        });
      }
      if (
        this.span &&
        "setStatus" in this.span &&
        typeof this.span.setStatus === "function"
      ) {
        // NOTE: Never set error status on spans/transactions
        // Performance traces should never be treated as errors in Sentry.
        // Only real exceptions captured via captureException() should create error events.
        // Using 'unknown' status for non-success spans prevents them from appearing as errors.
        const sentryStatus = status === "ok" ? "ok" : "unknown";
        (this.span as any).setStatus(sentryStatus);
      }
      if (
        this.span &&
        "end" in this.span &&
        typeof this.span.end === "function"
      ) {
        this.span.end();
      } else if (
        this.span &&
        "finish" in this.span &&
        typeof (this.span as any).finish === "function"
      ) {
        (this.span as any).finish();
      }
    } catch {}
  }
}

class SentryTransaction extends SentrySpan implements LoggerTransaction {
  constructor(span: Span | undefined, name: string) {
    super(span, name);
  }

  startChildSpan(
    name: string,
    attrs?: Record<string, string | number | boolean>
  ): LoggerSpan {
    try {
      if (
        this.span &&
        "startChild" in this.span &&
        typeof (this.span as any).startChild === "function"
      ) {
        const child = (this.span as any).startChild({
          op: name,
          description: name,
        });
        if (attrs && child) {
          Object.entries(attrs).forEach(([k, v]) => {
            try {
              if (
                child.setAttribute &&
                typeof child.setAttribute === "function"
              ) {
                child.setAttribute(k, v);
              }
            } catch {}
          });
        }
        return new SentrySpan(child, name);
      }
      return new ConsoleSpan(name);
    } catch {
      return new ConsoleSpan(name);
    }
  }
}

class SentryLogger implements Logger {
  init(): void {}

  setUser(
    user: { id?: string; email?: string; username?: string } | null
  ): void {
    try {
      Sentry.setUser(user);
    } catch {}
  }

  setTags(
    tags: Record<string, string | number | boolean | null | undefined>
  ): void {
    try {
      Object.entries(tags).forEach(([k, v]) => Sentry.setTag(k, String(v)));
    } catch {}
  }

  setExtras(extras: Record<string, unknown>): void {
    try {
      Object.entries(extras).forEach(([k, v]) =>
        Sentry.setContext(k, { value: v })
      );
    } catch {}
  }

  clearScope(): void {
    try {
      // Use getCurrentScope for newer Sentry versions
      if (
        "getCurrentScope" in Sentry &&
        typeof Sentry.getCurrentScope === "function"
      ) {
        const scope = Sentry.getCurrentScope();
        if (scope && "clear" in scope && typeof scope.clear === "function") {
          scope.clear();
        }
      } else if (
        "configureScope" in Sentry &&
        typeof (Sentry as any).configureScope === "function"
      ) {
        // Fallback for older versions
        (Sentry as any).configureScope((scope: any) => scope.clear());
      }
    } catch {}
  }

  addBreadcrumb(
    message: string,
    data?: Record<string, unknown>,
    level: LogLevel = "info",
    category?: string
  ): void {
    try {
      const sentry_level: SeverityLevel =
        level === "warning"
          ? "warning"
          : level === "error"
            ? "error"
            : level === "debug"
              ? "debug"
              : "info";
      const breadcrumb: Partial<Breadcrumb> = {
        message,
        data,
        level: sentry_level,
        category,
      };
      Sentry.addBreadcrumb(breadcrumb);
    } catch {}
  }

  private serializeContextObject(obj: object | Function | symbol): string {
    const seen = new WeakSet();

    return JSON.stringify(
      obj,
      (key, value): Record<string, string> | string => {
        if (typeof value === "function") {
          return "[Function]";
        }

        if (typeof value === "symbol") {
          return "[symbol]";
        }

        if (Error.isError(value)) {
          this.captureException(value);
          return {
            name: value.name,
            message: value.message,
            stack: value.stack ?? "",
          };
        }

        if (typeof value === "object" && value !== null) {
          if (seen.has(value)) return "[Circular]";
          seen.add(value);
        }

        return value;
      }
    );
  }

  private buildLogAttributes(
    ctx?: LogContext
  ): Record<string, string | number | boolean> | undefined {
    if (!ctx) return undefined;

    const attrs: Record<string, string | number | boolean> = {};
    if (ctx.tags) {
      for (const [k, v] of Object.entries(ctx.tags)) {
        if (v !== null && v !== undefined) attrs[k] = v;
      }
    }
    if (ctx.extras) {
      for (const [k, v] of Object.entries(ctx.extras)) {
        if (v === null) {
          attrs[k] = "null";
          continue;
        }
        switch (typeof v) {
          case "string":
          case "number":
          case "bigint":
          case "undefined":
          case "boolean":
            attrs[k] = String(v);
            break;
          case "object":
          case "function":
          case "symbol":
            attrs[k] = this.serializeContextObject(v);
            break;
          default:
            this.captureException(
              new Error(`Do not know how to serialize type: ${typeof v}`)
            );
            break;
        }
      }
    }
    return Object.keys(attrs).length > 0 ? attrs : undefined;
  }

  trace(message: string, ctx?: LogContext): void {
    try {
      Sentry.logger.trace(message, this.buildLogAttributes(ctx));
    } catch {}
  }
  debug(message: string, ctx?: LogContext): void {
    try {
      Sentry.logger.debug(message, this.buildLogAttributes(ctx));
    } catch {}
  }
  info(message: string, ctx?: LogContext): void {
    try {
      Sentry.logger.info(message, this.buildLogAttributes(ctx));
    } catch {}
  }
  warn(message: string, ctx?: LogContext): void {
    try {
      Sentry.logger.warn(message, this.buildLogAttributes(ctx));
    } catch {}
  }
  error(message: string, ctx?: LogContext): void {
    try {
      Sentry.logger.error(message, this.buildLogAttributes(ctx));
    } catch {}
  }
  fatal(message: string, ctx?: LogContext): void {
    try {
      Sentry.logger.fatal(message, this.buildLogAttributes(ctx));
    } catch {}
  }

  captureException(error: unknown, ctx?: LogContext): void {
    try {
      if (ctx?.tags || ctx?.extras || ctx?.fingerprint) {
        Sentry.withScope((scope) => {
          if (ctx.tags)
            Object.entries(ctx.tags).forEach(([k, v]) =>
              scope.setTag(k, String(v))
            );
          if (ctx.extras) scope.setContext("extras", ctx.extras);
          if (ctx.fingerprint) scope.setFingerprint(ctx.fingerprint);
          Sentry.captureException(error);
        });
      } else {
        Sentry.captureException(error);
      }
    } catch {}
  }

  captureMessage(
    message: string,
    level: LogLevel = "info",
    ctx?: LogContext
  ): void {
    try {
      const sentry_level: SeverityLevel =
        level === "warning"
          ? "warning"
          : level === "error"
            ? "error"
            : level === "debug"
              ? "debug"
              : "info";
      if (ctx?.tags || ctx?.extras || ctx?.fingerprint) {
        Sentry.withScope((scope) => {
          if (ctx.tags)
            Object.entries(ctx.tags).forEach(([k, v]) =>
              scope.setTag(k, String(v))
            );
          if (ctx.extras) scope.setContext("extras", ctx.extras);
          if (ctx.fingerprint) scope.setFingerprint(ctx.fingerprint);
          Sentry.captureMessage(message, sentry_level);
        });
      } else {
        Sentry.captureMessage(message, sentry_level);
      }
    } catch {}
  }

  startTransaction(
    name: string,
    attrs?: Record<string, string | number | boolean>
  ): LoggerTransaction {
    try {
      let span: Span | undefined;

      // Try proper transaction creation methods in order of preference
      // 1. First try startTransaction if available (older SDK versions)
      if (
        "startTransaction" in Sentry &&
        typeof (Sentry as any).startTransaction === "function"
      ) {
        span = (Sentry as any).startTransaction({ name, op: name });
      }
      // 2. Then try startInactiveSpan for manual control (newer SDK versions)
      else if (
        "startInactiveSpan" in Sentry &&
        typeof Sentry.startInactiveSpan === "function"
      ) {
        span = (Sentry as any).startInactiveSpan({ name, op: name });
      }
      // 3. Finally try startSpan as last resort
      else if (
        "startSpan" in Sentry &&
        typeof Sentry.startSpan === "function"
      ) {
        // Note: startSpan expects a callback but we want manual control
        // Create it without executing immediately for manual lifecycle management
        span = (Sentry as any).startSpan({ name, op: name }, () => undefined);
      }

      if (attrs && span) {
        Object.entries(attrs).forEach(([k, v]) => {
          try {
            if (
              span &&
              "setAttribute" in span &&
              typeof span.setAttribute === "function"
            ) {
              span.setAttribute(k, v);
            }
          } catch {}
        });
      }
      return new SentryTransaction(span, name);
    } catch {
      return new ConsoleTransaction(name);
    }
  }

  increment(
    _metric: string,
    _value: number = 1,
    _tags?: Record<string, string>
  ): void {
    // Optional: use Sentry metrics if available in SDK version
    try {
      if (
        "metrics" in Sentry &&
        Sentry.metrics &&
        typeof (Sentry.metrics as any).increment === "function"
      ) {
        (Sentry.metrics as any).increment(_metric, _value, { tags: _tags });
      }
    } catch {}
  }

  distribution(
    _metric: string,
    _value: number,
    _tags?: Record<string, string>
  ): void {
    try {
      if (
        "metrics" in Sentry &&
        Sentry.metrics &&
        typeof (Sentry.metrics as any).distribution === "function"
      ) {
        (Sentry.metrics as any).distribution(_metric, _value, { tags: _tags });
      }
    } catch {}
  }

  gauge(_metric: string, _value: number, _tags?: Record<string, string>): void {
    try {
      if (
        "metrics" in Sentry &&
        Sentry.metrics &&
        typeof (Sentry.metrics as any).gauge === "function"
      ) {
        (Sentry.metrics as any).gauge(_metric, _value, { tags: _tags });
      }
    } catch {}
  }

  flush(timeout_ms: number = 1500): Promise<boolean> {
    try {
      // Sentry.flush may require different arguments in different versions
      if (typeof Sentry.flush === "function") {
        return (Sentry as any).flush(timeout_ms) || Promise.resolve(true);
      }
      return Promise.resolve(true);
    } catch {
      return Promise.resolve(false);
    }
  }
}

function resolve_logger(): Logger {
  try {
    const environment = import.meta.env?.VITE_FE_ENV || import.meta.env?.MODE;
    const is_localhost =
      typeof window !== "undefined" && window.location.hostname === "localhost";
    const is_dev =
      import.meta.env?.MODE === "development" || environment === "development";

    const logger_instance =
      is_localhost || is_dev ? new ConsoleLogger() : new SentryLogger();

    // Set up automatic flush hooks for page unload events
    if (typeof window !== "undefined") {
      // Handle page unload (browser close/refresh)
      window.addEventListener("beforeunload", () => {
        logger_instance.flush(500).catch(() => {});
      });

      // Handle page hide (more reliable on mobile)
      window.addEventListener("pagehide", () => {
        logger_instance.flush(200).catch(() => {});
      });

      // Handle visibility change (tab switching)
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
          logger_instance.flush(200).catch(() => {});
        }
      });

      // Handle when the page is being unloaded (navigation)
      window.addEventListener("unload", () => {
        logger_instance.flush(100).catch(() => {});
      });
    }

    return logger_instance;
  } catch {
    return new ConsoleLogger();
  }
}

export const logger: Logger = resolve_logger();
